import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Empty, Input, Skeleton } from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CheckOutlined,
  LayoutOutlined,
  MessageOutlined,
  PaperClipOutlined,
  PlusOutlined,
  PushpinOutlined,
  ReadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { Button, Chip, PageTitle, Panel, Tag } from '../components/ds';
import { NewsCalendar } from '../components/news/NewsCalendar';
import { NewsViewerModal } from '../components/news/NewsViewerModal';
import { fmtSize } from '../components/news/NewsAttachments';
import { ALL, IMPORTANT, NEED_ACK, UNREAD, categoryCountsWithRead, filterFeed, isImportant } from '../components/news/news-utils';
import { api } from '../lib/api';
import { authApi } from '../lib/authApi';
import { newsApi, type NewsItemDto, type WikiFeedItemDto } from '../lib/wikiApi';
import './analytics/analytics.css';

const CRM_NEWS_URL = 'https://crm.brasol.ru/news';

/**
 * Совместимость: главная (W1) открывает новость этой модалкой. Новая
 * модалка живёт в components/news (порт CRM NewsViewerModal).
 */
export function NewsModal({ item, onClose }: { item: NewsItemDto | null; onClose: () => void }): JSX.Element {
  return <NewsViewerModal item={item} onClose={onClose} />;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : '';
}

/**
 * «Новости» HUB 2.0 (дизайн PageFeed, порт ленты CRM v2 frontend/src/pages/news/*
 * без импорта между пакетами): единая лента CRM-новостей и публикаций вики,
 * переключатель «Лента / Календарь», категории со счётчиками, поиск, карточки
 * (закреплено, категория, приоритет, «новое», вложения, опрос) и «Открыть и
 * подтвердить». Справа: ученику — «Ждут вашего подтверждения», УК —
 * «Ознакомление по публикациям» (CRM ack-list). Публиковать — только в CRM
 * («Новая публикация» → crm.brasol.ru/news?compose=1), своих write-эндпоинтов
 * у вики для новостей нет.
 */
export function NewsPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const isUk = !!me.data?.canAdmin;
  const { data, isLoading, error } = useQuery({ queryKey: ['wiki', 'news', 'feed'], queryFn: () => newsApi.feed({ limit: 100 }), retry: false });
  const [view, setView] = useState<'feed' | 'calendar'>('feed');
  const [chip, setChip] = useState<string>(ALL);
  const [q, setQ] = useState('');
  const [open, setOpenRaw] = useState<NewsItemDto | null>(null);

  const feed = useMemo(() => data?.items ?? [], [data]);
  const readSet = useMemo(() => {
    const s = new Set(data?.readIds ?? []);
    for (const f of feed) if (f.isRead) s.add(f.id);
    return s;
  }, [data, feed]);
  const cats = useMemo(() => categoryCountsWithRead(feed, readSet), [feed, readSet]);
  const shown = useMemo(() => filterFeed(feed, { chip, q, readIds: readSet }), [feed, chip, q, readSet]);
  const waiting = useMemo(() => feed.filter((f) => f.requiresAck && !readSet.has(f.id)), [feed, readSet]);
  const unreadN = feed.filter((f) => !readSet.has(f.id)).length;

  const openItem = (f: WikiFeedItemDto): void => {
    if (f.kind === 'publication' && f.slug) {
      navigate(`/article/${encodeURIComponent(f.slug)}`);
      return;
    }
    if (!f.news) return;
    setOpenRaw(f.news);
    // Открытие = ознакомление (CRM → news_reads, сотрудник iiko → wiki_news_reads).
    void newsApi
      .markRead(f.id)
      .then(() => qc.invalidateQueries({ queryKey: ['wiki', 'news'] }))
      .catch(() => undefined);
  };
  const openById = (id: string): void => {
    const f = feed.find((x) => x.id === id);
    if (f) openItem(f);
  };

  // УК: подтверждения по последним публикациям с подтверждением (CRM ack-list, news:edit_any).
  const ackTargets = useMemo(() => feed.filter((f) => f.kind === 'news' && f.requiresAck).slice(0, 6), [feed]);
  const ackQs = useQueries({
    queries: ackTargets.map((f) => ({
      queryKey: ['wiki', 'news', 'ack-list', f.id],
      queryFn: () => api.get<Array<{ partnerId: string }>>(`/news/${encodeURIComponent(f.id)}/ack-list`),
      enabled: isUk,
      retry: false,
      staleTime: 60_000,
    })),
  });
  const ackRows = ackTargets
    .map((f, i) => ({ f, n: ackQs[i]?.data ? new Set(ackQs[i].data!.map((r) => r.partnerId)).size : null }))
    .filter((r) => r.n != null);

  const is403 = (error as { status?: number } | null)?.status === 403;

  const header = (
    <PageTitle
      title="Новости"
      sub="Публикации для партнёров и команды — лента в HUB и CRM, дубли в Telegram и MAX; ознакомление засчитывается при открытии"
      style={{ marginBottom: 0 }}
      right={
        <>
          <Button variant="ghost" icon={<BellOutlined />} to="/notifications">
            Уведомления
          </Button>
          {isUk && (
            <>
              <Button variant="ghost" icon={<LayoutOutlined />} href={`${CRM_NEWS_URL}?tab=templates`}>
                Шаблоны
              </Button>
              <Button variant="primary" icon={<PlusOutlined />} href={`${CRM_NEWS_URL}?compose=1`}>
                Новая публикация
              </Button>
            </>
          )}
        </>
      }
    />
  );

  if (isLoading) {
    return (
      <div className="fk-adm-page">
        {header}
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="fk-adm-page">
        {header}
        <Alert
          type={is403 ? 'info' : 'error'}
          showIcon
          message={is403 ? 'Новости недоступны' : 'Не удалось загрузить новости'}
          description={is403 ? 'Войдите как партнёр или сотрудник точки, чтобы видеть ленту.' : (error as Error).message}
        />
      </div>
    );
  }

  return (
    <div className="fk-adm-page">
      {header}

      <div className="fk-adm-hint">
        <span style={{ display: 'inline-flex', border: '1px solid var(--fk-border)', borderRadius: 6, overflow: 'hidden' }}>
          {(
            [
              ['feed', 'Лента', <UnorderedListOutlined key="i" />],
              ['calendar', 'Календарь', <CalendarOutlined key="i" />],
            ] as const
          ).map(([k, l, ic]) => (
            <button
              key={k}
              type="button"
              aria-pressed={view === k}
              onClick={() => setView(k)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                border: 'none',
                font: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: view === k ? 'var(--fk-indigo)' : 'var(--fk-surface)',
                color: view === k ? '#fff' : 'var(--fk-text-2)',
              }}
            >
              {ic}
              {l}
            </button>
          ))}
        </span>
        <Chip active={chip === ALL} count={feed.length} onClick={() => setChip(ALL)}>Все</Chip>
        {unreadN > 0 && <Chip active={chip === UNREAD} count={unreadN} onClick={() => setChip(UNREAD)}>Новые</Chip>}
        {waiting.length > 0 && <Chip active={chip === NEED_ACK} count={waiting.length} onClick={() => setChip(NEED_ACK)}>Ждут подтверждения</Chip>}
        {feed.some(isImportant) && <Chip active={chip === IMPORTANT} onClick={() => setChip(IMPORTANT)}>Важные</Chip>}
        {cats.map((c) => (
          <Chip key={c.name} active={chip === c.name} count={c.count} onClick={() => setChip(c.name)}>
            {c.unread > 0 && <span className="fk-adm-dot" style={{ background: 'var(--fk-orange)', marginRight: 0 }} />}
            {c.name}
          </Chip>
        ))}
      </div>

      {view === 'calendar' ? (
        <NewsCalendar
          items={shown.map((f) => ({ id: f.id, title: f.title, publishedAt: f.publishedAt, requiresAck: f.requiresAck, isRead: readSet.has(f.id) }))}
          onOpen={openById}
        />
      ) : (
        <div className="fk-adm-grid2">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <Input allowClear prefix={<SearchOutlined />} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по новостям…" />
            {feed.length === 0 ? (
              <Panel>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Новостей пока нет" />
              </Panel>
            ) : shown.length === 0 ? (
              <Panel>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ничего не нашлось — сбросьте фильтр или поиск" />
              </Panel>
            ) : (
              shown.map((f) => <FeedCard key={`${f.kind}:${f.id}`} f={f} read={readSet.has(f.id)} onOpen={() => openItem(f)} />)
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {isUk && ackRows.length > 0 && (
              <Panel title="Ознакомление" sub="подтвердили партнёры · публикации с подтверждением" pad={0}>
                <div className="fk-adm-list">
                  {ackRows.map(({ f, n }) => (
                    <button key={f.id} type="button" className="fk-adm-list-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', textAlign: 'left', border: 'none', background: 'transparent', font: 'inherit', cursor: 'pointer' }} onClick={() => openItem(f)}>
                      <span className="fk-adm-ellipsis" style={{ fontWeight: 600 }}>{f.title}</span>
                      <b>{n}</b>
                    </button>
                  ))}
                </div>
              </Panel>
            )}
            {!isUk && (
              <Panel title="Ждут вашего подтверждения" sub="откройте публикацию — ознакомление засчитается" pad={0}>
                {waiting.length === 0 ? (
                  <div className="fk-adm-note" style={{ padding: 16 }}>Всё подтверждено</div>
                ) : (
                  <div className="fk-adm-list">
                    {waiting.slice(0, 8).map((f) => (
                      <button key={f.id} type="button" className="fk-adm-list-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', textAlign: 'left', border: 'none', background: 'transparent', font: 'inherit', cursor: 'pointer' }} onClick={() => openItem(f)}>
                        <span className="fk-adm-ellipsis" style={{ fontWeight: 600 }}>{f.title}</span>
                        <span style={{ color: 'var(--fk-orange)', fontSize: 12.5 }}>открыть →</span>
                      </button>
                    ))}
                  </div>
                )}
              </Panel>
            )}
            {feed.some((f) => f.isPinned) && (
              <Panel title="Закреплённые" icon={<PushpinOutlined />} pad={0}>
                <div className="fk-adm-list">
                  {feed
                    .filter((f) => f.isPinned)
                    .slice(0, 5)
                    .map((f) => (
                      <button key={f.id} type="button" className="fk-adm-list-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', textAlign: 'left', border: 'none', background: 'transparent', font: 'inherit', cursor: 'pointer' }} onClick={() => openItem(f)}>
                        <span className="fk-adm-ellipsis" style={{ fontWeight: 600 }}>{f.title}</span>
                        <span className="fk-adm-note">{fmtDate(f.publishedAt)}</span>
                      </button>
                    ))}
                </div>
              </Panel>
            )}
            <div className="fk-adm-note">
              Публикации дублируются в Telegram и MAX. При правке в CRM бот обновляет сообщение в чатах сам — в течение 1–2 минут.
            </div>
          </div>
        </div>
      )}

      <NewsViewerModal item={open} onClose={() => setOpenRaw(null)} canManage={isUk} />
    </div>
  );
}

/** Карточка ленты (NewsCard3 CRM v2 в стиле HUB 2.0). */
function FeedCard({ f, read, onOpen }: { f: WikiFeedItemDto; read: boolean; onOpen: () => void }): JSX.Element {
  const n = f.news;
  const isPub = f.kind === 'publication';
  const atts = n?.attachments ?? [];
  return (
    <Panel style={{ cursor: 'default' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        {f.isPinned && <Tag color="orange" icon={<PushpinOutlined />}>закреплено</Tag>}
        <Tag color="blue">{f.categoryName ?? (isPub ? 'Публикация' : 'Новость')}</Tag>
        {f.priority === 'REQUIRES_ACTION' ? <Tag color="red">требует действия</Tag> : f.priority === 'IMPORTANT' ? <Tag color="gold">важное</Tag> : null}
        {isPub && f.readingMinutes ? <Tag icon={<ReadOutlined />}>{f.readingMinutes} мин</Tag> : null}
        {!read && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--fk-orange)' }}>
            <span className="fk-adm-dot" style={{ background: 'var(--fk-orange)', marginRight: 0 }} />
            новое
          </span>
        )}
        <span className="fk-adm-note" style={{ marginLeft: 'auto' }}>{fmtDate(f.publishedAt)}</span>
      </div>
      <h3
        onClick={onOpen}
        style={{ margin: 0, fontSize: 17, fontWeight: 800, lineHeight: 1.3, color: 'var(--fk-indigo)', cursor: 'pointer' }}
      >
        {f.title}
      </h3>
      {f.excerpt && (
        <div onClick={onOpen} style={{ marginTop: 6, fontSize: 14, color: 'var(--fk-text-2)', lineHeight: 1.5, cursor: 'pointer', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {f.excerpt}
        </div>
      )}
      {atts.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {atts.slice(0, 4).map((a) => (
            <Tag key={a.id} icon={<PaperClipOutlined />}>
              {a.fileName.length > 32 ? `${a.fileName.slice(0, 30)}…` : a.fileName} · {fmtSize(a.fileSize)}
            </Tag>
          ))}
        </div>
      )}
      {n?.poll && (
        <div className="fk-adm-quote" style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <MessageOutlined style={{ color: 'var(--fk-indigo)' }} />
          <span style={{ flex: 1, minWidth: 0 }} className="fk-adm-ellipsis">
            <b>Опрос:</b> {n.poll.question}
          </span>
          <span className="fk-adm-note">{n.poll.totalVoters} голосов</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
        {f.requiresAck && read && <Tag color="green" icon={<CheckOutlined />}>вы ознакомились</Tag>}
        {f.requiresAck && !read && <span style={{ fontSize: 12.5, color: 'var(--fk-orange)', fontWeight: 600 }}>нужно подтвердить ознакомление</span>}
        <span style={{ flex: 1 }} />
        {f.requiresAck && !read ? (
          <Button size="sm" variant="primary" onClick={onOpen}>
            Открыть и подтвердить
          </Button>
        ) : (
          <Button size="sm" variant="text" onClick={onOpen}>
            {isPub ? 'Читать статью →' : 'Открыть →'}
          </Button>
        )}
      </div>
    </Panel>
  );
}
